import { useMemo, useState } from "react";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { showCustomConfirmDialog } from "../../dialogs/AppConfirmDialog";
import { dateUtility } from "../../../infrastructure/utilities/dateUtility";
import {
  BodyMetricRange,
  IBodyMetricEntry,
  IBodyMetricSummary,
  IBodyMetricsDataSource,
} from "../models/bodyMetricModels";
import {
  filterByRange,
  formatBodyMetricValue,
  withChanges,
} from "../utils/bodyMetricsUtility";

/** "Всички" first: it is the default view. */
export const BODY_METRIC_RANGES: { value: BodyMetricRange; label: string }[] = [
  { value: "all", label: "Всички" },
  { value: "1m", label: "1М" },
  { value: "3m", label: "3М" },
  { value: "6m", label: "6М" },
  { value: "1y", label: "1Г" },
];

/**
 * One metric's details: the range filter, the numbers behind its charts and tiles, and
 * deleting a reading (with a confirmation).
 */
export const useBodyMetricDetails = (
  summary: IBodyMetricSummary | null,
  dataSource: IBodyMetricsDataSource,
  visible: boolean,
) => {
  const [range, setRange] = useState<BodyMetricRange>("all");

  // Every opening starts on "Всички": a range picked for one metric doesn't carry over to the
  // next. Reset while rendering, so the stale range is never shown.
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) setRange("all");
  }

  const inRange = useMemo(
    () => (summary ? filterByRange(summary.history, range) : []),
    [summary, range],
  );

  const stats = useMemo(() => {
    if (!inRange.length) return null;
    const values = inRange.map((e) => e.value);
    return {
      first: inRange[0].value,
      change: inRange[inRange.length - 1].value - inRange[0].value,
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [inRange]);

  const changes = useMemo(() => withChanges(inRange).slice(1), [inRange]);

  // Newest first, each with its change from the reading before (across the whole history).
  const readings = useMemo(
    () => (summary ? withChanges(summary.history).reverse() : []),
    [summary],
  );

  const { mutate } = useApiMutation();

  const requestDelete = (entry: IBodyMetricEntry) => {
    showCustomConfirmDialog(
      `Да изтрием ли ${formatBodyMetricValue(entry.type, entry.value)} от ${dateUtility.formatDate(entry.measuredOn)}?`,
      () =>
        mutate({
          endpoint: `${dataSource.deleteEndpoint}?id=${entry.id}`,
          method: "delete",
          successMessage: "Стойността е изтрита.",
          invalidateKeys: [dataSource.listQueryKey],
        }),
      undefined,
      "warning",
      "Изтрий",
      "Отказ",
    );
  };

  return { range, setRange, inRange, stats, changes, readings, requestDelete };
};
